import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ShoppingCart } from 'lucide-react'
import type { Product } from '#bv/data/products'
import { DisplayPrice } from '#bv/components/DisplayPrice'
import { useCurrency } from '#bv/lib/currency/CurrencyContext'
import { addToCart, setCartItems, type CartItem } from '#bv/lib/cart'
import {
  normalizeRobuxGamepassLink,
  quoteRobuxPurchase,
  robuxCartLineName,
  ROBUX_MAX_AMOUNT,
  ROBUX_MIN_AMOUNT,
  ROBUX_PLATFORM_TAX_RATE,
  validateRobuxGamepassLinkMessage,
} from '#bv/lib/robuxProduct'
import { useProductStock } from '#bv/lib/useProductStock'

type Props = {
  product: Product & { robuxConfig: NonNullable<Product['robuxConfig']> }
  compact?: boolean
  /** Inside product checkout card — no outer panel chrome. */
  embedded?: boolean
}

const pillBase =
  'inline-flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-full font-semibold tracking-wide transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/45 disabled:cursor-not-allowed disabled:opacity-45 active:scale-[0.98]'

export function RobuxProductPurchase({ product, compact, embedded }: Props) {
  const { formatUsdCharge } = useCurrency()
  const navigate = useNavigate()
  const { inStock } = useProductStock()
  const cfg = product.robuxConfig
  const [amountInput, setAmountInput] = useState('1000')
  const [gamepassLinkInput, setGamepassLinkInput] = useState('')
  const [fieldError, setFieldError] = useState('')
  const [addedFlash, setAddedFlash] = useState(false)
  const productInStock = inStock(product.id)

  const parsed = useMemo(() => {
    const raw = amountInput.replace(/,/g, '').trim()
    if (!raw) return null
    const n = Number(raw)
    if (!Number.isFinite(n)) return null
    return Math.floor(n)
  }, [amountInput])

  const quote = useMemo(() => {
    if (parsed == null) return null
    return quoteRobuxPurchase(parsed, cfg, product.price)
  }, [parsed, cfg, product.price])

  const amountError = useMemo(() => {
    if (parsed == null) return amountInput.trim() ? 'Enter a valid whole number of Robux.' : 'Enter how many Robux you want.'
    if (parsed < ROBUX_MIN_AMOUNT || parsed > ROBUX_MAX_AMOUNT) {
      return `Amount must be between ${ROBUX_MIN_AMOUNT.toLocaleString()} and ${ROBUX_MAX_AMOUNT.toLocaleString()} Robux.`
    }
    return null
  }, [parsed, amountInput])

  const gamepassLinkError = useMemo(
    () => validateRobuxGamepassLinkMessage(gamepassLinkInput),
    [gamepassLinkInput],
  )

  const lineItem = (): CartItem | null => {
    if (!quote || amountError || gamepassLinkError) return null
    const gamepassLink = normalizeRobuxGamepassLink(gamepassLinkInput)
    if (!gamepassLink) return null
    return {
      productId: product.id,
      name: robuxCartLineName(quote),
      unitPrice: quote.usdPrice,
      quantity: 1,
      robuxAmount: quote.desiredRobux,
      robuxGamepassPrice: quote.gamepassPrice,
      robuxGamepassLink: gamepassLink,
    }
  }

  const runCheckout = (action: 'cart' | 'buy') => {
    if (!productInStock) return
    if (amountError) {
      setFieldError(amountError)
      return
    }
    if (gamepassLinkError) {
      setFieldError(gamepassLinkError)
      return
    }
    const item = lineItem()
    if (!item) {
      setFieldError('Enter a valid Robux amount and gamepass link.')
      return
    }
    setFieldError('')
    if (action === 'cart') {
      addToCart(item, 1)
      setAddedFlash(true)
      window.setTimeout(() => setAddedFlash(false), 1600)
    } else {
      setCartItems([item])
      void navigate({ to: '/cart', search: { checkout: '1' } })
    }
  }

  const pad = compact ? 'py-2.5 text-xs sm:py-3 sm:text-sm' : 'py-3 text-sm sm:min-h-[2.875rem] sm:text-[0.9375rem]'
  const taxPct = Math.round(
    (cfg.platformTaxRate ?? (cfg.creatorShare != null ? 1 - cfg.creatorShare : ROBUX_PLATFORM_TAX_RATE)) * 100,
  )

  const shellClass = embedded
    ? ''
    : `rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-black/25 ${
        compact ? 'p-3' : 'p-4 sm:p-5'
      }`

  return (
    <div className={shellClass}>
      <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-[#f8e7bf]">
        <span className="font-semibold text-[#ffdca8]">Payment methods: PayPal F&amp;F or crypto.</span> Robux orders are
        fulfilled manually after payment clears. Card and MoneyMotion are not available for this purchase.
      </div>

      <label className="mt-5 block text-[0.62rem] font-bold uppercase tracking-[0.16em] text-[#7a92b8]">
        Robux you want (after {taxPct}% tax)
        <input
          type="number"
          inputMode="numeric"
          min={ROBUX_MIN_AMOUNT}
          max={ROBUX_MAX_AMOUNT}
          step={1}
          required
          value={amountInput}
          onChange={(e) => {
            setAmountInput(e.target.value)
            setFieldError('')
          }}
          className="bv-input mt-2 h-11 w-full rounded-xl px-3 text-base font-semibold tabular-nums text-[#f0f6ff] sm:h-12"
          placeholder={`${ROBUX_MIN_AMOUNT} – ${ROBUX_MAX_AMOUNT.toLocaleString()}`}
        />
      </label>
      <p className="mt-2 text-[0.7rem] leading-relaxed text-[#5a6d8a]">
        Max {ROBUX_MAX_AMOUNT.toLocaleString()} Robux per order. We charge in USD; create a gamepass at the price below,
        then paste its link so we can purchase it after payment.
      </p>

      <label className="mt-4 block text-[0.62rem] font-bold uppercase tracking-[0.16em] text-[#7a92b8]">
        Gamepass link
        <input
          type="url"
          inputMode="url"
          required
          value={gamepassLinkInput}
          onChange={(e) => {
            setGamepassLinkInput(e.target.value)
            setFieldError('')
          }}
          className="bv-input mt-2 h-11 w-full rounded-xl px-3 text-sm text-[#f0f6ff] sm:h-12"
          placeholder="https://www.roblox.com/game-pass/…"
          autoComplete="off"
        />
      </label>
      <p className="mt-2 text-[0.7rem] leading-relaxed text-[#5a6d8a]">
        Create the gamepass in Roblox at the price shown below, set it for sale, then copy the share link from the
        gamepass page.
      </p>

      {quote && !amountError ? (
        <dl className="mt-4 space-y-2.5 rounded-xl border border-[rgba(0, 191, 255,0.2)] bg-[rgba(0, 191, 255,0.06)] px-3.5 py-3 text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[#7a92b8]">Set gamepass price to</dt>
            <dd className="font-display text-lg font-bold tabular-nums text-[#f0f6ff]">
              {quote.gamepassPrice.toLocaleString()} R$
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[#7a92b8]">Roblox tax ({taxPct}%)</dt>
            <dd className="font-display text-base font-semibold tabular-nums text-[#7a92b8]">
              −{quote.platformTaxRobux.toLocaleString()} R$
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-[#7a92b8]">You receive (after tax)</dt>
            <dd className="font-display text-lg font-bold tabular-nums text-[#d4e8ff]">
              {quote.robuxAfterTax.toLocaleString()} R$
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-3 border-t border-white/[0.08] pt-2.5">
            <dt className="font-semibold text-[#8ecfff]">You pay</dt>
            <dd className="font-display text-xl font-bold tabular-nums text-[#93c5fd]">
              <DisplayPrice usd={quote.usdPrice} />
              <span className="ml-1 text-xs font-medium text-[#5a6d8a]">({formatUsdCharge(quote.usdPrice)} USD)</span>
            </dd>
          </div>
        </dl>
      ) : null}

      {fieldError || amountError || gamepassLinkError ? (
        <p className="mt-3 text-xs font-medium text-[#fda4af]" role="alert">
          {fieldError || amountError || gamepassLinkError}
        </p>
      ) : null}

      {!productInStock ? (
        <button
          type="button"
          disabled
          className={`${pillBase} ${pad} mt-4 border border-white/12 bg-white/[0.03] text-[#5a6d8a]`}
        >
          Out of stock
        </button>
      ) : (
        <div className={`mt-4 flex w-full flex-col gap-2 ${compact ? '' : 'sm:flex-row sm:gap-3'}`}>
          <button
            type="button"
            onClick={() => runCheckout('buy')}
            disabled={Boolean(amountError || gamepassLinkError)}
            className={`btn-primary ${pillBase} ${pad} flex-1 shadow-[0_6px_28px_rgba(0, 191, 255,0.35)]`}
          >
            Continue to checkout
          </button>
          <button
            type="button"
            onClick={() => runCheckout('cart')}
            disabled={Boolean(amountError || gamepassLinkError)}
            className={`${pillBase} ${pad} flex-1 border border-white/20 bg-white/[0.03] text-[#f0f6ff] hover:border-[rgba(0, 191, 255,0.45)] hover:bg-[rgba(0, 191, 255,0.1)] ${
              addedFlash ? 'ring-2 ring-emerald-400/45' : ''
            }`}
          >
            <ShoppingCart className="h-[1.1em] w-[1.1em] shrink-0 opacity-90" strokeWidth={2} aria-hidden />
            {addedFlash ? 'Added' : 'Add to order'}
          </button>
        </div>
      )}
    </div>
  )
}
